<!-- eslint-disable -->
<template>
  <div class="friend-button">
    <!-- Loading -->
    <v-btn v-if="loading" variant="outlined" size="small" disabled>
      <v-progress-circular indeterminate size="16" width="2" class="mr-2"></v-progress-circular>
      {{ $t('common.loading') }}
    </v-btn>

    <!-- Already friends -->
    <v-btn
      v-else-if="friendshipStatus === 'accepted'"
      variant="outlined"
      color="success"
      size="small"
      @click="showUnfriendMenu = true"
    >
      <v-icon start size="small">mdi-account-check</v-icon>
      {{ $t('friends.friends') }}
      <v-menu v-model="showUnfriendMenu" activator="parent">
        <v-list>
          <v-list-item @click="removeFriend">
            <template v-slot:prepend>
              <v-icon color="error">mdi-account-remove</v-icon>
            </template>
            <v-list-item-title class="text-error">{{ $t('friends.unfriend') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-btn>

    <!-- Request sent by me (pending) -->
    <v-btn
      v-else-if="friendshipStatus === 'pending' && isRequester"
      variant="outlined"
      color="grey"
      size="small"
      @click="cancelRequest"
      :loading="actionLoading"
    >
      <v-icon start size="small">mdi-clock-outline</v-icon>
      {{ $t('friends.requestSent') }}
    </v-btn>

    <!-- Request received (pending) -->
    <div v-else-if="friendshipStatus === 'pending' && !isRequester" class="d-flex align-center" style="gap: 0.5rem;">
      <v-btn
        color="primary"
        size="small"
        @click="acceptRequest"
        :loading="actionLoading"
      >
        <v-icon start size="small">mdi-check</v-icon>
        {{ $t('friends.accept') }}
      </v-btn>
      <v-btn
        variant="outlined"
        size="small"
        @click="declineRequest"
        :loading="declineLoading"
      >
        {{ $t('friends.decline') }}
      </v-btn>
    </div>

    <!-- No relationship - can add friend -->
    <v-btn
      v-else-if="friendshipStatus === 'none' || friendshipStatus === 'declined'"
      color="primary"
      variant="outlined"
      size="small"
      @click="sendRequest"
      :loading="actionLoading"
    >
      <v-icon start size="small">mdi-account-plus</v-icon>
      {{ $t('friends.addFriend') }}
    </v-btn>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, onMounted, watch, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '../services/api'

const { t } = useI18n()

// Get refresh function from App
const refreshFriendRequests = inject('refreshFriendRequests', () => {})

const props = defineProps({
  userId: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['statusChanged'])

const loading = ref(true)
const actionLoading = ref(false)
const declineLoading = ref(false)
const friendshipStatus = ref('none')
const isRequester = ref(false)
const friendshipId = ref(null)
const showUnfriendMenu = ref(false)

const checkFriendshipStatus = async () => {
  loading.value = true
  try {
    const response = await api.get('/friends', { params: { check: props.userId } })
    friendshipStatus.value = response.data.friendshipStatus
    isRequester.value = response.data.isRequester
    friendshipId.value = response.data.friendship?.friendshipId || null
  } catch (error) {
    console.error('Error checking friendship:', error)
    friendshipStatus.value = 'none'
  } finally {
    loading.value = false
  }
}

const sendRequest = async () => {
  actionLoading.value = true
  try {
    const response = await api.post('/friends', { userId: props.userId })
    if (response.data.success) {
      friendshipStatus.value = response.data.status
      isRequester.value = true
      emit('statusChanged', response.data.status)
    }
  } catch (error) {
    console.error('Error sending request:', error)
    alert(error.response?.data?.error || t('common.error'))
  } finally {
    actionLoading.value = false
  }
}

const acceptRequest = async () => {
  actionLoading.value = true
  try {
    const response = await api.put('/friends', {
      friendshipId: friendshipId.value,
      action: 'accept'
    })
    if (response.data.success) {
      friendshipStatus.value = 'accepted'
      emit('statusChanged', 'accepted')
      refreshFriendRequests() // Update nav badge
    }
  } catch (error) {
    console.error('Error accepting request:', error)
    alert(error.response?.data?.error || t('common.error'))
  } finally {
    actionLoading.value = false
  }
}

const declineRequest = async () => {
  declineLoading.value = true
  try {
    const response = await api.put('/friends', {
      friendshipId: friendshipId.value,
      action: 'decline'
    })
    if (response.data.success) {
      friendshipStatus.value = 'declined'
      emit('statusChanged', 'declined')
      refreshFriendRequests() // Update nav badge
    }
  } catch (error) {
    console.error('Error declining request:', error)
    alert(error.response?.data?.error || t('common.error'))
  } finally {
    declineLoading.value = false
  }
}

const cancelRequest = async () => {
  actionLoading.value = true
  try {
    const response = await api.delete(`/friends?userId=${props.userId}`)
    if (response.data.success) {
      friendshipStatus.value = 'none'
      isRequester.value = false
      friendshipId.value = null
      emit('statusChanged', 'none')
    }
  } catch (error) {
    console.error('Error cancelling request:', error)
    alert(error.response?.data?.error || t('common.error'))
  } finally {
    actionLoading.value = false
  }
}

const removeFriend = async () => {
  showUnfriendMenu.value = false
  actionLoading.value = true
  try {
    const response = await api.delete(`/friends?userId=${props.userId}`)
    if (response.data.success) {
      friendshipStatus.value = 'none'
      isRequester.value = false
      friendshipId.value = null
      emit('statusChanged', 'none')
    }
  } catch (error) {
    console.error('Error removing friend:', error)
    alert(error.response?.data?.error || t('common.error'))
  } finally {
    actionLoading.value = false
  }
}

watch(() => props.userId, () => {
  checkFriendshipStatus()
})

onMounted(() => {
  checkFriendshipStatus()
})
</script>

<style scoped>
.friend-button {
  display: inline-flex;
}
</style>